// Customer ticket portal sign-in on the classic /login page (docs/decisions/0009): an email code or a verified
// external identity, explicit consent for a new account, then the portal at /app/tickets.
import {postGraphql} from "./ExternalAuthWebHelper";
import {WEB_APP_PREFIX} from "./ReturnUrlHelper";

export const PORTAL_HOME = `${WEB_APP_PREFIX}/tickets`;
export const PORTAL_CODE_LENGTH = 6;

export interface PortalSignInResult {
    token?: string | null;
    registrationTicket?: string | null;
    email?: string | null;
    displayName?: string | null;
    requiresRegistration: boolean;
}

/** A registration the user still has to consent to, from an email code or an external provider. */
export interface PortalRegistration {
    ticket: string;
    email?: string | null;
    displayName?: string | null;
}

function decodePayload(token: string): Record<string, unknown> | undefined {
    const segment = token.split(".")[1];
    if (!segment) return undefined;
    try {
        const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
        const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
    } catch {
        return undefined;
    }
}

/** Portal-only tokens carry AccountKind=Portal; they open only the portal in /app. Read without verifying. */
export function isPortalToken(token: string | null | undefined): boolean {
    return !!token && decodePayload(token)?.AccountKind === "Portal";
}

/** Where a portal account goes after login: a remembered portal page of /app, else its tickets. */
export function portalLoginRedirect(triedAccessing: string | null, origin: string): string {
    if (!triedAccessing) return PORTAL_HOME;
    try {
        const url = new URL(triedAccessing, origin);
        const path = url.pathname;
        if (url.origin !== origin) return PORTAL_HOME;
        if (path === PORTAL_HOME || path.startsWith(`${PORTAL_HOME}/`) || path === `${WEB_APP_PREFIX}/profile`) {
            return `${path}${url.search}${url.hash}`;
        }
    } catch {
        return PORTAL_HOME;
    }
    return PORTAL_HOME;
}

export function normalizePortalCode(value: string): string {
    return value.replace(/\D/g, "").slice(0, PORTAL_CODE_LENGTH);
}

export function looksLikeEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** The registration a sign-in result asks consent for, or undefined when it carries a token. */
export function registrationFromResult(result: PortalSignInResult): PortalRegistration | undefined {
    if (result.token || !result.requiresRegistration || !result.registrationTicket) return undefined;
    return {ticket: result.registrationTicket, email: result.email, displayName: result.displayName};
}

export interface PortalLoginConfig {
    enabled: boolean;
    /** Whether the public entry point (the customer sign-in button) is shown; false during a quiet soft launch. */
    showEntryPoints: boolean;
}

export const loadPortalConfig = async (): Promise<PortalLoginConfig> => {
    const data = await postGraphql<{portalConfig?: {enabled: boolean; showEntryPoints?: boolean | null} | null}>(`
        query PortalConfig {
            portalConfig {
                enabled
                showEntryPoints
            }
        }
    `);
    const enabled = data.portalConfig?.enabled === true;
    return {enabled, showEntryPoints: enabled && data.portalConfig?.showEntryPoints === true};
};

/** `/login?portal=1` is the deliberate entry to the customer sign-in while its button is hidden (soft launch). */
export function isDeliberatePortalEntry(search: string): boolean {
    return new URLSearchParams(search).get("portal") === "1";
}

export const startPortalEmailSignIn = async (email: string): Promise<string> => {
    const data = await postGraphql<{startPortalEmailSignIn: {challengeId: string}}>(
        `
        mutation StartPortalEmailSignIn($email: String!) {
            startPortalEmailSignIn(email: $email) {
                challengeId
            }
        }
    `,
        {email: email.trim()},
    );
    return data.startPortalEmailSignIn.challengeId;
};

export const verifyPortalEmailCode = async (
    challengeId: string,
    code: string,
): Promise<PortalSignInResult> => {
    const data = await postGraphql<{verifyPortalEmailCode: PortalSignInResult}>(
        `
        mutation VerifyPortalEmailCode($challengeId: UUID!, $code: String!) {
            verifyPortalEmailCode(challengeId: $challengeId, code: $code) {
                token
                registrationTicket
                email
                displayName
                requiresRegistration
            }
        }
    `,
        {challengeId, code},
    );
    return data.verifyPortalEmailCode;
};

export const completePortalRegistration = async (registrationTicket: string): Promise<string> => {
    const data = await postGraphql<{completePortalRegistration: PortalSignInResult}>(
        `
        mutation CompletePortalRegistration($registrationTicket: String!) {
            completePortalRegistration(registrationTicket: $registrationTicket, acceptTerms: true) {
                token
            }
        }
    `,
        {registrationTicket},
    );
    const token = data.completePortalRegistration.token;
    if (!token) throw new Error("Registracija nije vratila prijavu.");
    return token;
};
